import React, { useState } from 'react';
import { rhuService } from '../../../services/rhuService';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Login as LoginIcon,
  Info as InfoIcon,
  HealthAndSafety as HealthIcon,
  Lock as LockIcon
} from '@mui/icons-material';

const RHUAccessLogin = ({ onAccessGranted }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter an access code');
      return;
    }

    setLoading(true);
    setError('');

    const result = await rhuService.verifyAccessCode(code);

    if (result.success) {
      // Store access code session
      sessionStorage.setItem('rhu_access', JSON.stringify({
        code: code,
        accessType: 'code',
        timestamp: new Date().toISOString()
      }));
      
      onAccessGranted({
        accessType: 'code',
        firstname: 'RHU',
        lastname: 'Staff',
        role: 'RHU Staff'
      });
    } else {
      setError(result.error || 'Invalid access code');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        p: 2
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(60px)',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(60px)',
          zIndex: 0
        }}
      />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Badge/Icon */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'transparent',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                mb: 2,
                boxShadow: '0 10px 30px rgba(239, 68, 68, 0.3)'
              }}
            >
              <HealthIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" fontWeight="bold" align="center" color="text.primary" gutterBottom>
              Rural Health Unit
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight="medium">
              LGU Concepcion, Romblon
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Access Code"
                placeholder="ENTER ACCESS CODE"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setError('');
                }}
                disabled={loading}
                autoFocus
                error={Boolean(error)}
                helperText={error || "Enter your RHU staff access code"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.1rem',
                    letterSpacing: '0.15em',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !code.trim()}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #664190 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)'
                }
              }}
            >
              {loading ? 'Verifying...' : 'Access System'}
            </Button>
          </form>

          <Box sx={{ my: 3, borderBottom: 1, borderColor: 'divider' }} />

          {/* Information Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              mb: 2,
              display: 'flex',
              gap: 1.5
            }}
          >
            <InfoIcon color="primary" sx={{ fontSize: 20, mt: 0.2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              This access is for RHU staff only. Contact your administrator if you need an access code.
            </Typography>
          </Paper>

          {/* Security Notice */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.5),
              background: alpha(theme.palette.warning.light, 0.3)
            }}
          >
            <LockIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
            <Typography variant="caption" color="warning.dark" fontWeight="medium">
              Keep your access code confidential
            </Typography>
          </Paper>
        </Paper>
      </Container>
    </Box>
  );
};

export default RHUAccessLogin;
